import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_VAT_CONFIGURATION, UPDATE_VAT_CONFIGURATION } from '../../graphql/vat-configuration';

const VATConfigurationModal = ({ isOpen, onClose }) => {
    const [config, setConfig] = useState({
        vatPercentage: 15.0,
        vatType: 'STANDARD',
        displayVatSeparately: true,
        changeReason: ''
    });

    const { data, loading, error } = useQuery(GET_VAT_CONFIGURATION, {
        skip: !isOpen,
        onCompleted: (data) => {
            if (data?.vatConfiguration) {
                setConfig({
                    vatPercentage: data.vatConfiguration.vatPercentage || 15.0,
                    vatType: data.vatConfiguration.vatType || 'STANDARD',
                    displayVatSeparately: data.vatConfiguration.displayVatSeparately !== false,
                    changeReason: ''
                });
            }
        }
    });

    const [updateVatConfig] = useMutation(UPDATE_VAT_CONFIGURATION, {
        onCompleted: () => {
            alert('VAT configuration updated successfully');
            onClose();
        },
        onError: (error) => {
            alert(`Error updating VAT configuration: ${error.message}`);
        }
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!config.changeReason.trim()) {
            alert('Please provide a reason for the VAT configuration change');
            return;
        }

        updateVatConfig({
            variables: {
                input: {
                    vatPercentage: parseFloat(config.vatPercentage),
                    vatType: config.vatType,
                    displayVatSeparately: config.displayVatSeparately,
                    changeReason: config.changeReason.trim()
                }
            }
        });
    };

    const handleConfigChange = (field, value) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                <div className="mt-3">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">
                            VAT Configuration
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <span className="sr-only">Close</span>
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Modal Content */}
                    {loading ? (
                        <div className="flex justify-center items-center py-8">
                            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mt-4" role="alert">
                            <strong className="font-bold">Error!</strong>
                            <span className="block sm:inline"> {error.message}</span>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="mt-4 space-y-6">
                            {/* VAT Percentage */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    VAT Percentage (%)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={config.vatPercentage}
                                    onChange={(e) => handleConfigChange('vatPercentage', e.target.value)}
                                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    required
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Standard VAT rate in South Africa is 15%
                                </p>
                            </div>

                            {/* VAT Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    VAT Type
                                </label>
                                <select
                                    value={config.vatType}
                                    onChange={(e) => handleConfigChange('vatType', e.target.value)}
                                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    required
                                >
                                    <option value="STANDARD">Standard VAT</option>
                                    <option value="ZERO_RATED">Zero-Rated</option>
                                    <option value="EXEMPT">Exempt</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Select the appropriate VAT type for your business
                                </p>
                            </div>

                            {/* Display VAT Separately */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">
                                        Display VAT Separately
                                    </label>
                                    <p className="text-sm text-gray-500">
                                        Show VAT as a separate line item on invoices
                                    </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={config.displayVatSeparately}
                                        onChange={(e) => handleConfigChange('displayVatSeparately', e.target.checked)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                            </div>

                            {/* Change Reason */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reason for Change
                                </label>
                                <textarea
                                    value={config.changeReason}
                                    onChange={(e) => handleConfigChange('changeReason', e.target.value)}
                                    rows="3"
                                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder="Describe the reason for this VAT configuration change..."
                                    required
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Required for audit trail purposes
                                </p>
                            </div>

                            {/* Preview Section */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <h4 className="text-sm font-medium text-blue-900 mb-2">Preview</h4>
                                <div className="text-sm text-blue-800">
                                    <p>VAT Type: {config.vatType.replace('_', ' ')}</p>
                                    <p>VAT Rate: {config.vatPercentage}%</p>
                                    <p>Display: {config.displayVatSeparately ? 'Separate line item' : 'Included in price'}</p>
                                    <div className="mt-2 p-2 bg-white rounded border">
                                        <p className="font-medium">Example Invoice Line:</p>
                                        {config.displayVatSeparately ? (
                                            <>
                                                <p>Subtotal: R 100.00</p>
                                                <p>VAT ({config.vatPercentage}%): R {(100 * config.vatPercentage / 100).toFixed(2)}</p>
                                                <p>Total: R {(100 + (100 * config.vatPercentage / 100)).toFixed(2)}</p>
                                            </>
                                        ) : (
                                            <>
                                                <p>Amount: R {(100 + (100 * config.vatPercentage / 100)).toFixed(2)} (VAT inclusive)</p>
                                                <p>VAT ({config.vatPercentage}%): R {(100 * config.vatPercentage / 100).toFixed(2)}</p>
                                                <p>Subtotal: R 100.00</p>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Save Configuration
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VATConfigurationModal;